"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Popconfirm,
  Button,
  Empty,
  InputNumber,
  message,
  Checkbox,
  Spin,
  Tag,
  Tooltip,
  Collapse,
  Badge,
  Input
} from "antd"
import {
  ShopOutlined,
  CheckCircleOutlined,
  StarOutlined,
  InfoCircleOutlined,
  SendOutlined,
  ExportOutlined
} from "@ant-design/icons"
import { useAuth } from "../contexts/authContext/index"
import { detectCartProviders, sendCartQuoteRequests } from "../services/QuoteRequestServices"
import "../assets/styles/Cart.css"

const { TextArea } = Input
const { Panel } = Collapse

const Cart = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [detectingProviders, setDetectingProviders] = useState(false)
  const [providers, setProviders] = useState([])
  const [selectedProviders, setSelectedProviders] = useState([])
  const [notes, setNotes] = useState("")
  const [step, setStep] = useState("cart") // "cart" | "providers" | "sending"
  const navigate = useNavigate()
  const { userLoggedIn, currentUser, getToken } = useAuth()

  useEffect(() => {
    const updateCart = () => {
      const storedItems = JSON.parse(localStorage.getItem("cart") || "{}")
      const flattenedItems = Object.entries(storedItems).flatMap(([productId, product]) =>
        (product.measures || []).map((measure) => ({
          id: productId,
          name: product.name,
          image: (product.images || [])[0],
          measure: measure.measure,
          quantity: measure.quantity,
        })),
      )
      setItems(flattenedItems)
    }

    window.addEventListener("cartUpdated", updateCart)
    updateCart()
    return () => window.removeEventListener("cartUpdated", updateCart)
  }, [])

  useEffect(() => {
    if (userLoggedIn && currentUser?.photoURL === "vendedor") {
      navigate("/profile")
    } else if (userLoggedIn && currentUser?.photoURL === "admin") {
      navigate("/admin")
    }
  }, [navigate, userLoggedIn, currentUser])

  const handleInputChange = useCallback(
    (id, measure, newQuantity) => {
      const sanitizedQuantity = Math.max(1, newQuantity || 1)
      const updatedItems = items.map((item) =>
        item.id === id && item.measure === measure ? { ...item, quantity: sanitizedQuantity } : item,
      )
      setItems(updatedItems)

      const cartObject = JSON.parse(localStorage.getItem("cart") || "{}")
      if (cartObject[id]) {
        cartObject[id].measures = (cartObject[id].measures || []).map((m) =>
          m.measure === measure ? { ...m, quantity: sanitizedQuantity } : m,
        )
        localStorage.setItem("cart", JSON.stringify(cartObject))
        window.dispatchEvent(new Event("cartUpdated"))
      }
    },
    [items],
  )

  const removeItem = useCallback(
    (id, measure) => {
      const updatedItems = items.filter((item) => !(item.id === id && item.measure === measure))
      setItems(updatedItems)

      const cartObject = JSON.parse(localStorage.getItem("cart") || "{}")
      if (cartObject[id]) {
        cartObject[id].measures = (cartObject[id].measures || []).filter((m) => m.measure !== measure)
        if (cartObject[id].measures.length === 0) {
          delete cartObject[id]
        }
        localStorage.setItem("cart", JSON.stringify(cartObject))
        window.dispatchEvent(new Event("cartUpdated"))
      }
    },
    [items],
  )

  const handleReturnToStore = () => {
    navigate("/")
  }

  // Preparar productos para la API
  const getCartProductsForAPI = () => {
    const cart = JSON.parse(localStorage.getItem("cart") || "{}")
    return Object.entries(cart).map(([productId, product]) => ({
      id: productId,
      name: product.name,
      measures: (product.measures || []).map((measure) => ({
        measure: measure.measure,
        quantity: measure.quantity,
      })),
    }))
  }

  // Paso 1: Detectar proveedores
  const handleDetectProviders = async () => {
    setDetectingProviders(true)
    try {
      const token = await getToken()
      const cartProducts = getCartProductsForAPI()

      const response = await detectCartProviders(token, cartProducts)

      if (response.success && response.data) {
        setProviders(response.data.providers || [])
        // Preseleccionar proveedores originales
        const originalProviders = (response.data.providers || [])
          .filter((p) => p.is_original)
          .map((p) => p.provider_id)
        setSelectedProviders(originalProviders)
        setStep("providers")
      } else {
        message.error("No se encontraron proveedores disponibles")
      }
    } catch (error) {
      console.error("Error detecting providers:", error)
      message.error(error.message || "Error al detectar proveedores")
    } finally {
      setDetectingProviders(false)
    }
  }

  // Paso 2: Enviar solicitudes
  const handleSendQuoteRequests = async () => {
    if (selectedProviders.length === 0) {
      message.warning("Selecciona al menos un proveedor")
      return
    }

    setLoading(true)
    setStep("sending")

    try {
      const token = await getToken()
      const cartProducts = getCartProductsForAPI()

      const response = await sendCartQuoteRequests(
        token,
        cartProducts,
        selectedProviders,
        "", // specifications
        notes
      )

      if (response.success) {
        const notified =
          response?.data?.providers_notified ?? response?.providers_notified ?? selectedProviders.length
        message.success(`Solicitud enviada a ${notified} proveedores`)
        localStorage.removeItem("cart")
        window.dispatchEvent(new Event("cartUpdated"))
        navigate("/mis-cotizaciones")
      } else {
        message.error("Error al enviar las solicitudes")
        setStep("providers")
      }
    } catch (error) {
      console.error("Error sending quote requests:", error)
      message.error(error.message || "Error al enviar solicitudes")
      setStep("providers")
    } finally {
      setLoading(false)
    }
  }

  const handleProviderToggle = (providerId) => {
    setSelectedProviders((prev) =>
      prev.includes(providerId)
        ? prev.filter((id) => id !== providerId)
        : [...prev, providerId]
    )
  }

  const handleSelectAll = () => {
    if (selectedProviders.length === providers.length) {
      setSelectedProviders([])
    } else {
      setSelectedProviders(providers.map((p) => p.provider_id))
    }
  }

  const totalUnits = items.reduce((sum, item) => sum + (item.quantity || 0), 0)
  const uniqueProducts = items.length

  // Vista de selección de proveedores
  if (step === "providers" || step === "sending") {
    return (
      <div className="cart__page">
        <div className="layout">
          <div className="layout__content cart__layout-inner">
            <div className="cart__header">
              <div>
                <p className="cart__eyebrow">Paso 2 de 2</p>
                <h1 className="cart__title">Selecciona Proveedores</h1>
                <p className="cart__subtitle">
                  Elige a qué proveedores deseas enviar tu solicitud de cotización.
                  Los proveedores originales de cada producto están marcados con una estrella.
                </p>
              </div>
              <Button
                type="default"
                onClick={() => setStep("cart")}
                disabled={loading}
                className="cart__ghost-button"
              >
                Volver al carrito
              </Button>
            </div>

            {step === "sending" ? (
              <div style={{ textAlign: "center", padding: 60 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>Enviando solicitudes...</p>
              </div>
            ) : (
              <div className="cart__content">
                <div className="cart__list">
                  {/* Selector de todos */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                      padding: "12px 16px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: 8,
                    }}
                  >
                    <Checkbox
                      checked={selectedProviders.length === providers.length}
                      indeterminate={
                        selectedProviders.length > 0 &&
                        selectedProviders.length < providers.length
                      }
                      onChange={handleSelectAll}
                    >
                      Seleccionar todos ({providers.length})
                    </Checkbox>
                    <Badge
                      count={selectedProviders.length}
                      style={{ backgroundColor: "#1890ff" }}
                    >
                      <Tag color="blue">Seleccionados</Tag>
                    </Badge>
                  </div>

                  {/* Lista de proveedores */}
                  {providers.map((provider) => (
                    <article
                      className="cart__item-card"
                      key={provider.provider_id}
                      style={{
                        border: selectedProviders.includes(provider.provider_id)
                          ? "2px solid #1890ff"
                          : "1px solid #e8e8e8",
                        cursor: "pointer",
                      }}
                      onClick={() => handleProviderToggle(provider.provider_id)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "0 16px",
                        }}
                      >
                        <Checkbox
                          checked={selectedProviders.includes(provider.provider_id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleProviderToggle(provider.provider_id)}
                        />
                      </div>
                      <div className="cart__item-body" style={{ flex: 1 }}>
                        <div className="cart__item-header">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <ShopOutlined style={{ fontSize: 20 }} />
                            <div>
                              <h3 style={{ margin: 0 }}>
                                {provider.provider_name || provider.provider_company || "Proveedor"}
                                {provider.is_original && (
                                  <Tooltip title="Proveedor original de tus productos seleccionados">
                                    <StarOutlined
                                      style={{ color: "#faad14", marginLeft: 8 }}
                                    />
                                  </Tooltip>
                                )}
                              </h3>
                              {provider.provider_company && (
                                <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                                  {provider.provider_company}
                                </p>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            {(() => {
                              const hasOriginal = provider.products.some(p => p.is_original_product)
                              const hasSimilar = provider.products.some(p => !p.is_original_product)

                              if (hasOriginal && hasSimilar) {
                                return (
                                  <>
                                    <Tag color="gold" icon={<StarOutlined />}>Original</Tag>
                                    <Tag color="blue">+ Similar</Tag>
                                  </>
                                )
                              } else if (hasOriginal) {
                                return <Tag color="gold" icon={<StarOutlined />}>Original</Tag>
                              } else {
                                return <Tag color="blue">Similar</Tag>
                              }
                            })()}
                            {provider.covers_all && (
                              <Tag color="green" icon={<CheckCircleOutlined />}>
                                Cubre todos
                              </Tag>
                            )}
                          </div>
                        </div>
                        <div className="cart__item-footer" onClick={(e) => e.stopPropagation()}>
                          <Collapse ghost size="small">
                            <Panel
                              header={
                                <span style={{ fontSize: 12 }}>
                                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                                  {(() => {
                                    // Contar productos originales vs similares
                                    const originalCount = provider.products.filter(p => p.is_original_product).length
                                    const similarCount = provider.products.filter(p => !p.is_original_product).length

                                    if (originalCount > 0 && similarCount > 0) {
                                      return `${originalCount} original(es), ${similarCount} similar(es)`
                                    } else if (originalCount > 0) {
                                      return `${originalCount} producto(s) de tu carrito`
                                    } else {
                                      return `${provider.unique_matching_products || similarCount} producto(s) similar(es)`
                                    }
                                  })()}
                                </span>
                              }
                              key="1"
                            >
                              <div style={{ fontSize: 12 }}>
                                {/* Productos originales del proveedor */}
                                {provider.products.filter(p => p.is_original_product).length > 0 && (
                                  <div style={{ marginBottom: 8 }}>
                                    <strong style={{ color: "#d48806" }}>Productos exactos:</strong>
                                    <ul style={{ margin: "4px 0 0 16px", paddingLeft: 0 }}>
                                      {provider.products.filter(p => p.is_original_product).map((prod, idx) => (
                                        <li key={idx} style={{ marginBottom: 4 }}>
                                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <span>
                                              {prod.product_name}
                                              <Tag size="small" color="gold" style={{ marginLeft: 4 }}>
                                                Exacto
                                              </Tag>
                                            </span>
                                            <Tooltip title="Ver producto">
                                              <Button
                                                type="link"
                                                size="small"
                                                icon={<ExportOutlined />}
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  window.open(`/producto/${prod.product_id}`, "_blank")
                                                }}
                                                style={{ padding: 0, height: "auto" }}
                                              />
                                            </Tooltip>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* Productos similares del proveedor */}
                                {provider.products.filter(p => !p.is_original_product).length > 0 && (
                                  <div>
                                    {provider.products.filter(p => p.is_original_product).length > 0 && (
                                      <strong style={{ color: "#1890ff" }}>Productos similares:</strong>
                                    )}
                                    {(() => {
                                      // Agrupar productos similares únicos del proveedor
                                      const uniqueProviderProducts = new Map()
                                      provider.products.filter(p => !p.is_original_product).forEach((prod) => {
                                        if (prod.matching_product_ids) {
                                          prod.matching_product_ids.forEach((matchId, idx) => {
                                            const matchName = prod.matching_products?.[idx] || "Producto similar"
                                            if (!uniqueProviderProducts.has(matchId)) {
                                              uniqueProviderProducts.set(matchId, {
                                                id: matchId,
                                                name: matchName,
                                                coversCartProducts: []
                                              })
                                            }
                                            const existing = uniqueProviderProducts.get(matchId)
                                            if (!existing.coversCartProducts.find(p => p.id === prod.product_id)) {
                                              existing.coversCartProducts.push({
                                                id: prod.product_id,
                                                name: prod.product_name
                                              })
                                            }
                                          })
                                        }
                                      })

                                      return Array.from(uniqueProviderProducts.values()).map((providerProd, idx) => (
                                        <div key={idx} style={{
                                          marginTop: 8,
                                          padding: 8,
                                          background: "#f0f5ff",
                                          borderRadius: 4
                                        }}>
                                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <span style={{ fontWeight: 500 }}>
                                              {providerProd.name}
                                              <Tag size="small" color="blue" style={{ marginLeft: 4 }}>
                                                Similar
                                              </Tag>
                                            </span>
                                            <Tooltip title="Ver este producto">
                                              <Button
                                                type="link"
                                                size="small"
                                                icon={<ExportOutlined />}
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  window.open(`/producto/${providerProd.id}`, "_blank")
                                                }}
                                                style={{ padding: 0, height: "auto" }}
                                              />
                                            </Tooltip>
                                          </div>
                                          <div style={{ marginTop: 4, color: "#666", fontSize: 11 }}>
                                            Alternativa para: {providerProd.coversCartProducts.map(p => p.name).join(", ")}
                                          </div>
                                        </div>
                                      ))
                                    })()}
                                  </div>
                                )}
                              </div>
                            </Panel>
                          </Collapse>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                <aside className="cart__summary">
                  <p className="cart__eyebrow">Resumen</p>
                  <h2 className="cart__summary-title">Enviar Solicitud</h2>
                  <div className="cart__summary-row">
                    <span>Productos</span>
                    <strong>{uniqueProducts}</strong>
                  </div>
                  <div className="cart__summary-row">
                    <span>Unidades totales</span>
                    <strong>{totalUnits}</strong>
                  </div>
                  <div className="cart__summary-row">
                    <span>Proveedores seleccionados</span>
                    <strong>{selectedProviders.length}</strong>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <label style={{ fontSize: 12, color: "#666" }}>
                      Notas adicionales (opcional)
                    </label>
                    <TextArea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Agrega información adicional para los proveedores..."
                      rows={3}
                      style={{ marginTop: 4 }}
                    />
                  </div>

                  <p className="cart__summary-helper" style={{ marginTop: 16 }}>
                    Los proveedores seleccionados recibirán tu solicitud y podrán
                    enviarte sus cotizaciones.
                  </p>

                  <button
                    className="cart__button cart__button--primary"
                    onClick={handleSendQuoteRequests}
                    disabled={loading || selectedProviders.length === 0}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <SendOutlined />
                    {loading
                      ? "Enviando..."
                      : `Enviar a ${selectedProviders.length} proveedor(es)`}
                  </button>
                </aside>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Vista principal del carrito
  return (
    <div className="cart__page">
      <div className="layout">
        <div className="layout__content cart__layout-inner">
          <div className="cart__header">
            <div>
              <p className="cart__eyebrow">Tu selección actual</p>
              <h1 className="cart__title">Carrito de Compras</h1>
              <p className="cart__subtitle">
                Revisa las cantidades y solicita cotizaciones a los proveedores disponibles.
              </p>
            </div>
            <Button type="default" onClick={handleReturnToStore} className="cart__ghost-button">
              Seguir explorando
            </Button>
          </div>

          {items.length === 0 ? (
            <Empty description={<span>Tu carrito está vacío</span>}>
              <Button type="primary" onClick={handleReturnToStore} className="cart__empty-button">
                Ir a la tienda
              </Button>
            </Empty>
          ) : (
            <div className="cart__content">
              <div className="cart__list">
                {items.map((item) => (
                  <article className="cart__item-card" key={`${item.id}-${item.measure}`}>
                    <div className="cart__item-image">
                      <img src={item.image || "/placeholder.svg"} alt={item.name} />
                    </div>
                    <div className="cart__item-body">
                      <div className="cart__item-header">
                        <div>
                          <h3>{item.name}</h3>
                          <p>Medida seleccionada: {item.measure}</p>
                        </div>
                        <Popconfirm
                          title="¿Eliminar este producto del carrito?"
                          onConfirm={() => removeItem(item.id, item.measure)}
                          okText="Sí"
                          cancelText="No"
                          placement="left"
                        >
                          <button className="cart__pill cart__pill--danger">Eliminar</button>
                        </Popconfirm>
                      </div>
                      <div className="cart__item-footer">
                        <div>
                          <span className="cart__label">Cantidad</span>
                          <InputNumber
                            min={1}
                            value={item.quantity}
                            onChange={(value) => handleInputChange(item.id, item.measure, value)}
                            className="cart__quantity-input"
                          />
                        </div>
                        <p className="cart__note">No hay límite: el proveedor definirá la disponibilidad.</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              <aside className="cart__summary">
                <p className="cart__eyebrow">Resumen</p>
                <h2 className="cart__summary-title">Solicitud de Cotización</h2>
                <div className="cart__summary-row">
                  <span>Productos únicos</span>
                  <strong>{uniqueProducts}</strong>
                </div>
                <div className="cart__summary-row">
                  <span>Total de unidades</span>
                  <strong>{totalUnits}</strong>
                </div>
                <p className="cart__summary-helper">
                  En el siguiente paso podrás elegir a qué proveedores enviar tu solicitud de cotización.
                </p>
                <button
                  className="cart__button cart__button--primary"
                  onClick={handleDetectProviders}
                  disabled={detectingProviders}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {detectingProviders ? (
                    <>
                      <Spin size="small" />
                      Buscando proveedores...
                    </>
                  ) : (
                    <>
                      <ShopOutlined />
                      Buscar Proveedores
                    </>
                  )}
                </button>
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Cart
